import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Clock, ArrowRight, AlertTriangle, User } from 'lucide-react'
import axios from '../../../../config/axios'

interface StepHistoryEntry {
  id: number
  stepId: number
  stepName: string
  enteredAt: string
  exitedAt: string | null
  userId: number | null
  user?: { id: number; name: string; picture?: string } | null
}

interface StepTimelineProps {
  ticketId: number
  serviceId?: number | null
  isProductionView?: boolean
}

const formatDuration = (seconds: number) => {
  const d = Math.floor(seconds / 86400)
  const h = Math.floor((seconds % 86400) / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (d > 0) return `${d}d ${h}h`
  if (h > 0) return `${h}h ${m}m`
  if (m > 0) return `${m}m`
  return `${seconds}s`
}

const formatDateTime = (iso: string) => {
  const d = new Date(iso)
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' }) +
    ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

const calcElapsed = (enteredAt: string, exitedAt: string | null) => {
  const start = new Date(enteredAt).getTime()
  const end = exitedAt ? new Date(exitedAt).getTime() : Date.now()
  return Math.max(0, Math.floor((end - start) / 1000))
}

const TimelineContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`

const ProductionTimeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  overflow-x: auto;
  padding-top: 360px;
  margin-top: -340px;
  padding-bottom: 8px;
  pointer-events: none;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`

const ProductionTrack = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0;
  padding: 10px 4px 6px;
  min-width: max-content;
  width: 100%;
  pointer-events: auto;
`

const ProductionTrackBase = styled.div`
  position: absolute;
  left: 60px;
  right: 60px;
  height: 4px;
  background-color: #d1d5db;
  border-radius: 999px;
  top: 50%;
  transform: translateY(-50%);
`

const ProductionTrackActive = styled.div<{ $width: number }>`
  position: absolute;
  left: 60px;
  height: 4px;
  background-color: #0090D3;
  border-radius: 999px;
  top: 50%;
  transform: translateY(-50%);
  width: ${({ $width }) => `calc((100% - 120px) * (${$width} / 100))`};
`

const TooltipContainer = styled.div`
  display: none;
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #ffffff;
  color: #1e293b;
  padding: 12px;
  border-radius: 8px;
  font-size: 11px;
  width: 290px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  z-index: 100;
  text-align: left;
  line-height: 1.4;
  pointer-events: auto;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-width: 6px;
    border-style: solid;
    border-color: #ffffff transparent transparent transparent;
  }
`

const ProductionStageItem = styled.div<{ $isFirst?: boolean; $isLast?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  min-width: 120px;
  z-index: 10;
  position: relative;

  &:hover {
    z-index: 2000;
  }

  &:hover > .stage-tooltip {
    display: block;
  }

  ${({ $isFirst }) => $isFirst && `
    & > .stage-tooltip {
      left: 10px;
      transform: none;

      &::after {
        left: 50px;
        transform: translateX(-50%);
      }
    }
  `}

  ${({ $isLast }) => $isLast && `
    & > .stage-tooltip {
      left: auto;
      right: 10px;
      transform: none;

      &::after {
        left: auto;
        right: 50px;
        transform: translateX(50%);
      }
    }
  `}
`

const TooltipHeader = styled.div`
  font-weight: 700;
  font-size: 12px;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 6px;
  margin-bottom: 6px;
  color: #0090D3;
`

const TooltipRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 3px;
`

const TooltipLabel = styled.span`
  color: #64748b;
`

const TooltipValue = styled.span`
  font-weight: 600;
  color: #0f172a;
`

const TooltipDivider = styled.div`
  height: 1px;
  background-color: #e2e8f0;
  margin: 6px 0;
`

const TooltipLogTitle = styled.div`
  font-weight: 700;
  color: #64748b;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
`

const TooltipLogItem = styled.div`
  font-size: 10px;
  color: #334155;
  margin-bottom: 2px;
`

const TooltipLogList = styled.div`
  max-height: 90px;
  overflow-y: auto;
  padding-right: 6px;
  margin-top: 4px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 2px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 2px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`

const ProductionStageDot = styled.div<{ $status: 'done' | 'current' | 'upcoming' }>`
  width: ${({ $status }) => ($status === 'current' ? '14px' : '12px')};
  height: ${({ $status }) => ($status === 'current' ? '14px' : '12px')};
  border-radius: 50%;
  background-color: ${({ $status }) => ($status === 'upcoming' ? '#e5e7eb' : '#0090D3')};
  border: 2px solid ${({ $status }) => ($status === 'current' ? '#006fa3' : $status === 'done' ? '#0090D3' : '#d1d5db')};
  box-shadow: ${({ $status }) => ($status === 'current' ? '0 0 0 4px rgba(0, 144, 211, 0.2)' : 'none')};
`

const ProductionStageLabel = styled.span<{ $status: 'done' | 'current' | 'upcoming' }>`
  margin-top: 8px;
  font-size: 11px;
  font-weight: ${({ $status }) => ($status === 'current' ? 700 : 500)};
  color: ${({ $status }) => ($status === 'upcoming' ? '#9ca3af' : '#1f2937')};
  text-align: center;
  max-width: 120px;
  line-height: 1.2;
  word-break: break-word;
`

const TimelineEntry = styled.div`
  display: flex;
  gap: 12px;
  position: relative;
  padding-bottom: 16px;

  &:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 13px;
    top: 28px;
    bottom: 0;
    width: 2px;
    background-color: #e0e0e0;
  }
`

const TimelineDot = styled.div<{ $active: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background-color: ${({ $active }) => $active ? '#0090D3' : '#e8e8e8'};
  color: ${({ $active }) => $active ? '#fff' : '#9ca3af'};
  z-index: 1;
`

const TimelineBody = styled.div`
  flex: 1;
  min-width: 0;
  padding-top: 2px;
`

const TimelineStepName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #303030;
`

const TimelineMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 4px;
`

const TimelineTime = styled.span<{ $expired: boolean }>`
  font-size: 11px;
  font-weight: 500;
  color: ${({ $expired }) => $expired ? '#BF383B' : '#6b7280'};
  display: flex;
  align-items: center;
  gap: 3px;
`

const TimelineDate = styled.span`
  font-size: 11px;
  color: #9ca3af;
`

const TimelineUser = styled.span`
  font-size: 11px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 3px;
`

const ReworkBadge = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: #92400e;
  background-color: #fef3c7;
  padding: 1px 6px;
  border-radius: 4px;
`

const EmptyState = styled.div`
  font-size: 13px;
  color: #9ca3af;
  padding: 8px 0;
`

const formatTooltipTime = (seconds: number) => {
  const absoluteSeconds = Math.max(0, Math.floor(seconds))
  const h = Math.floor(absoluteSeconds / 3600)
  const m = Math.floor((absoluteSeconds % 3600) / 60)
  const s = absoluteSeconds % 60

  const pad = (num: number) => String(num).padStart(2, '0')
  return `${pad(h)}h${pad(m)}min${pad(s)}seg`
}

const formatTooltipDate = (isoString?: string | null) => {
  if (!isoString) return ''
  const d = new Date(isoString)
  const dateStr = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
  const timeStr = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  return `${dateStr} às ${timeStr}`
}

const StepTimeline: React.FC<StepTimelineProps> = ({ ticketId, serviceId, isProductionView }) => {
  const [history, setHistory] = useState<StepHistoryEntry[]>([])
  const [productionStages, setProductionStages] = useState<Array<{
    id: number
    name: string
    index: number
    completed: boolean
    executionTime: number | null
    setupTime: number | null
    userName: string | null
    catalogExecutionTime: number | null
  }>>([])
  const [stageEntries, setStageEntries] = useState<Record<number, any[]>>({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true
    const fetchProductionStages = async () => {
      try {
        if (!serviceId) {
          if (mounted) setProductionStages([])
          return
        }
        const res = await axios.get(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/${serviceId}`
        )
        const rawStages = res.data?.Service_Stage || res.data?.service_Stage || []
        const mapped = (Array.isArray(rawStages) ? rawStages : []).map((stage: any) => ({
          id: stage.id,
          name: stage.Stage?.name || stage.stage?.name || 'Etapa',
          index: Number.isFinite(Number(stage.index)) ? Number(stage.index) : 0,
          completed: Boolean(stage.completed),
          executionTime: stage.executionTime ?? stage.Stage?.executionTime ?? stage.stage?.executionTime ?? null,
          setupTime: stage.setupTime ?? stage.Stage?.setupTime ?? stage.stage?.setupTime ?? null,
          userName: stage.User?.name || stage.user?.name || null,
          catalogExecutionTime: stage.Stage?.executionTime ?? stage.stage?.executionTime ?? null,
        }))
        mapped.sort((a, b) => a.index - b.index)
        if (mounted) setProductionStages(mapped)

        const entriesPromises = mapped.map(async (stage) => {
          try {
            const entriesRes = await axios.get(
              `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/from-app/time-entries/${stage.id}`
            )
            return { stageId: stage.id, entries: entriesRes.data || [] }
          } catch {
            return { stageId: stage.id, entries: [] }
          }
        })
        const allEntriesResults = await Promise.all(entriesPromises)
        const entriesMap: Record<number, any[]> = {}
        allEntriesResults.forEach((result) => {
          entriesMap[result.stageId] = result.entries
        })
        if (mounted) setStageEntries(entriesMap)
      } catch {
        if (mounted) setProductionStages([])
      }
    }

    const fetchHistory = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/step-history`
        )
        if (mounted && res.data?.data) {
          setHistory(res.data.data)
        }
      } catch {
        if (mounted) setHistory([])
      }
    }

    const run = async () => {
      if (isProductionView) {
        await Promise.all([fetchProductionStages(), fetchHistory()])
      } else {
        await fetchHistory()
      }
    }

    run().finally(() => {
      if (mounted) setLoading(false)
    })

    return () => { mounted = false }
  }, [ticketId, serviceId, isProductionView])

  if (loading) return <EmptyState>Carregando histórico...</EmptyState>

  if (isProductionView) {
    if (productionStages.length === 0) return <EmptyState>Sem etapas cadastradas</EmptyState>

    const firstOpenIndex = productionStages.findIndex((stage) => !stage.completed)
    const allCompleted = firstOpenIndex === -1
    const currentIndex = allCompleted ? productionStages.length - 1 : firstOpenIndex
    const progressPercent = productionStages.length <= 1
      ? 0
      : Math.max(0, Math.min(100, (currentIndex / (productionStages.length - 1)) * 100))

    return (
      <ProductionTimeline>
        <ProductionTrack>
          <ProductionTrackBase />
          <ProductionTrackActive $width={progressPercent} />
          {productionStages.map((stage, index) => {
            const status: 'done' | 'current' | 'upcoming' = allCompleted
              ? (index === currentIndex ? 'current' : 'done')
              : index < currentIndex
              ? 'done'
              : index === currentIndex
              ? 'current'
              : 'upcoming'

            const historiesForStage = history.filter(
              (h) => h.stepName.toLowerCase().trim() === stage.name.toLowerCase().trim()
            )
            const historyForStage = historiesForStage.length > 0 ? historiesForStage[historiesForStage.length - 1] : null

            const entries = stageEntries[stage.id] || []
            const sortedEntries = [...entries].sort(
              (a, b) => new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime()
            )

            let executionSeconds = 0
            let setupSeconds = 0
            let runStart: number | null = null
            let pauseStart: number | null = null

            for (const entry of sortedEntries) {
              const t = new Date(entry.recordedAt).getTime()
              if (!entry.isPause) {
                runStart = t
                if (pauseStart !== null) {
                  setupSeconds += Math.max(0, Math.floor((t - pauseStart) / 1000))
                  pauseStart = null
                }
              } else {
                pauseStart = t
                if (runStart !== null) {
                  executionSeconds += Math.max(0, Math.floor((t - runStart) / 1000))
                  runStart = null
                }
              }
            }

            if (runStart !== null && !stage.completed) {
              executionSeconds += Math.max(0, Math.floor((Date.now() - runStart) / 1000))
            }
            if (pauseStart !== null && !stage.completed) {
              setupSeconds += Math.max(0, Math.floor((Date.now() - pauseStart) / 1000))
            }

            const totalSeconds = executionSeconds + setupSeconds
            const maxExecutionSeconds = stage.catalogExecutionTime

            const firstEntryTime = sortedEntries[0]?.recordedAt
            const lastEntryTime = sortedEntries[sortedEntries.length - 1]?.recordedAt

            let stageTotalSeconds = 0
            if (historiesForStage.length > 0) {
              historiesForStage.forEach((h) => {
                const start = new Date(h.enteredAt).getTime()
                const end = h.exitedAt ? new Date(h.exitedAt).getTime() : Date.now()
                stageTotalSeconds += Math.max(0, Math.floor((end - start) / 1000))
              })
            } else if (sortedEntries.length > 0) {
              const start = new Date(sortedEntries[0].recordedAt).getTime()
              const end = stage.completed && sortedEntries.length > 1
                ? new Date(sortedEntries[sortedEntries.length - 1].recordedAt).getTime()
                : Date.now()
              stageTotalSeconds = Math.max(0, Math.floor((end - start) / 1000))
            } else {
              stageTotalSeconds = totalSeconds
            }

            const enteredLabel = historyForStage
              ? `Entrou em ${stage.name}: ${formatTooltipDate(historyForStage.enteredAt)}`
              : (firstEntryTime
                  ? `Entrou em ${stage.name}: ${formatTooltipDate(firstEntryTime)}`
                  : `Entrou em ${stage.name}: N/A`)

            const exitedLabel = historyForStage && historyForStage.exitedAt
              ? `Saiu de ${stage.name}: ${formatTooltipDate(historyForStage.exitedAt)}`
              : (stage.completed
                  ? `Saiu de ${stage.name}: ${lastEntryTime ? formatTooltipDate(lastEntryTime) : 'Concluído'}`
                  : `Saiu de ${stage.name}: Em andamento`)

            const logLines = sortedEntries.map((entry) => {
              const timeStr = new Date(entry.recordedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
              const dateStr = new Date(entry.recordedAt).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
              const action = entry.isPause ? 'Pausou' : 'Iniciou'
              const userName = entry.user?.name || entry.userName || stage.userName || 'Sistema'
              return `${dateStr} às ${timeStr} - ${action} por ${userName}`
            })

            return (
              <ProductionStageItem
                key={stage.id}
                $isFirst={index === 0}
                $isLast={index === productionStages.length - 1 && productionStages.length > 1}
              >
                <ProductionStageDot $status={status} />
                <ProductionStageLabel $status={status}>{stage.name}</ProductionStageLabel>

                {stage.completed && (
                  <TooltipContainer className="stage-tooltip">
                    <TooltipHeader>{stage.name}</TooltipHeader>
                    <div style={{ fontSize: '10px', color: '#0284c7', marginBottom: '4px', fontWeight: 600 }}>
                      {enteredLabel}
                    </div>
                    <div style={{ fontSize: '10px', color: '#059669', marginBottom: '6px', fontWeight: 600 }}>
                      {exitedLabel}
                    </div>
                    <TooltipDivider />
                    <TooltipRow>
                      <TooltipLabel>Total executando:</TooltipLabel>
                      <TooltipValue>{formatTooltipTime(executionSeconds)}</TooltipValue>
                    </TooltipRow>
                    <TooltipRow>
                      <TooltipLabel>Total pausado:</TooltipLabel>
                      <TooltipValue>{formatTooltipTime(setupSeconds)}</TooltipValue>
                    </TooltipRow>
                    <TooltipRow>
                      <TooltipLabel>Tempo total:</TooltipLabel>
                      <TooltipValue>{formatTooltipTime(totalSeconds)}</TooltipValue>
                    </TooltipRow>
                    <TooltipRow>
                      <TooltipLabel>Tempo do serviço:</TooltipLabel>
                      <TooltipValue>
                        {maxExecutionSeconds && maxExecutionSeconds > 0
                          ? formatTooltipTime(maxExecutionSeconds)
                          : 'Não cadastrado'}
                      </TooltipValue>
                    </TooltipRow>
                    <TooltipRow>
                      <TooltipLabel>Tempo na etapa:</TooltipLabel>
                      <TooltipValue>{formatTooltipTime(stageTotalSeconds)}</TooltipValue>
                    </TooltipRow>

                    {logLines.length > 0 && (
                      <>
                        <TooltipDivider />
                        <TooltipLogTitle>Histórico de Operações</TooltipLogTitle>
                        <TooltipLogList>
                          {logLines.map((line, idx) => (
                            <TooltipLogItem key={idx}>{line}</TooltipLogItem>
                          ))}
                        </TooltipLogList>
                      </>
                    )}
                  </TooltipContainer>
                )}
              </ProductionStageItem>
            )
          })}
        </ProductionTrack>
      </ProductionTimeline>
    )
  }

  if (history.length === 0) return <EmptyState>Sem histórico de etapas</EmptyState>

  const stepVisitCount: Record<string, number> = {}
  const reversed = [...history].reverse()

  return (
    <TimelineContainer>
      {reversed.map((entry) => {
        const key = String(entry.stepId)
        stepVisitCount[key] = (stepVisitCount[key] || 0) + 1
        const isRework = stepVisitCount[key] > 1
        const isActive = entry.exitedAt === null
        const elapsed = calcElapsed(entry.enteredAt, entry.exitedAt)
        const isExpired = false

        return (
          <TimelineEntry key={entry.id}>
            <TimelineDot $active={isActive}>
              <Clock size={14} />
            </TimelineDot>
            <TimelineBody>
              <TimelineStepName>
                {entry.stepName}
                {isRework && <ReworkBadge>Retrabalho</ReworkBadge>}
              </TimelineStepName>
              <TimelineMeta>
                <TimelineTime $expired={isExpired}>
                  {isActive ? <AlertTriangle size={11} /> : <Clock size={11} />}
                  {formatDuration(elapsed)}
                </TimelineTime>
                <TimelineDate>{formatDateTime(entry.enteredAt)}</TimelineDate>
                {entry.exitedAt && (
                  <>
                    <ArrowRight size={11} color='#9ca3af' />
                    <TimelineDate>{formatDateTime(entry.exitedAt)}</TimelineDate>
                  </>
                )}
                {entry.user && (
                  <TimelineUser>
                    <User size={11} />
                    {entry.user.name}
                  </TimelineUser>
                )}
              </TimelineMeta>
            </TimelineBody>
          </TimelineEntry>
        )
      })}
    </TimelineContainer>
  )
}

export default StepTimeline
